/**
 * Tech Adoption Timeline Widget (issue #1654, part of epic #1650)
 *
 * Shows monthly cumulative per-technology adoption counts over the
 * documented `?months=12` window, matching `homepage-dashboard-spec.md`'s
 * "Technology Adoption Timeline" widget. Administrator only — see
 * `backend/src/graphql/resolvers/techAdoptionTimeline.ts`. Follows the
 * single-query + `useStatisticsWidget` pattern shared by
 * `DependencyHealthWidget`.
 */

import { useTechAdoptionTimeline, type TechAdoptionTimelineMonth } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './TechAdoptionTimelineWidget.css';

/** Matches the spec's documented `?months=12` example. */
const TIMELINE_MONTHS = 12;

const WIDGET_CONFIG: WidgetConfig = {
  id: 'tech-adoption-timeline',
  title: 'Tech Adoption Timeline',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface TechAdoptionTimelineStats {
  timeline: TechAdoptionTimelineMonth[];
}

type TechAdoptionTimelineQueryData = ReturnType<typeof useTechAdoptionTimeline>['data'];

function extractTechAdoptionTimelineStats(
  data: TechAdoptionTimelineQueryData
): TechAdoptionTimelineStats | null {
  if (!data?.techAdoptionTimeline) {
    return null;
  }
  return data.techAdoptionTimeline;
}

export function TechAdoptionTimelineWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useTechAdoptionTimeline(undefined, TIMELINE_MONTHS),
    extractTechAdoptionTimelineStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.timeline.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="tech-adoption-timeline-widget__empty">
          <IconElement name="show_chart" className="tech-adoption-timeline-widget__empty-icon" />
          <p className="forge-typography--body2">No adoption history available</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <ul className="tech-adoption-timeline-widget__list" data-testid="tech-adoption-timeline-list">
        {stats.timeline.map((month) => (
          <li
            key={month.month}
            className="tech-adoption-timeline-widget__month"
            data-testid="tech-adoption-timeline-month"
          >
            <span className="tech-adoption-timeline-widget__month-label forge-typography--label2">
              {month.month}
            </span>
            {month.technologies.length === 0 ? (
              <span className="tech-adoption-timeline-widget__month-empty forge-typography--caption">
                No adoption yet
              </span>
            ) : (
              <div className="tech-adoption-timeline-widget__chips">
                {month.technologies.map((entry) => (
                  <span key={entry.name} className="tech-adoption-timeline-widget__chip">
                    {entry.name}: {entry.count}
                  </span>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </DashboardWidget>
  );
}
